import React, { useEffect, useState, useCallback } from 'react';
import { Save, RotateCcw, Ticket, BadgePercent, Eye, EyeOff } from 'lucide-react';
import HeroCouponPromo from '../../components/HeroCouponPromo';

const PATH = '/super-admin/promo-banner';

export default function PromoBannerTab({ api }) {
  const [data, setData] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [toast, setToast] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api('GET', PATH);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load promo banner');
    }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true); setError(null);
    try {
      const payload = {
        enabled: !!data.enabled,
        code: (data.code || '').trim().toUpperCase(),
        headline: data.headline,
        subline: data.subline,
        badge_text: data.badge_text,
        terms: data.terms,
      };
      const res = await api('PATCH', PATH, payload);
      setData(res.data);
      setToast('Saved — live on the homepage now.');
      setTimeout(() => setToast(null), 2500);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (!data) {
    return <div className="p-8 text-slate-500" data-testid="promo-loading">Loading promo banner…</div>;
  }

  return (
    <div className="p-4 sm:p-6 space-y-6 max-w-5xl" data-testid="promo-banner-tab">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h2 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Ticket size={22} className="text-amber-600" /> Hero Coupon Banner
          </h2>
          <p className="text-sm text-slate-500 mt-1">
            Control the live discount banner on the homepage hero. Changes are live within seconds — no deployment needed.
          </p>
        </div>
        <button
          onClick={save}
          disabled={saving}
          data-testid="promo-save-btn"
          className="inline-flex items-center gap-2 px-5 h-11 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white font-semibold shadow-md disabled:opacity-60"
        >
          <Save size={16} /> {saving ? 'Saving…' : 'Save Changes'}
        </button>
      </div>

      {toast && <div className="bg-emerald-50 border border-emerald-200 text-emerald-800 px-4 py-2 rounded-md text-sm" data-testid="promo-toast">{toast}</div>}
      {error && <div className="bg-rose-50 border border-rose-200 text-rose-800 px-4 py-2 rounded-md text-sm" data-testid="promo-error">{error}</div>}

      {/* Enabled toggle */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center justify-between gap-4">
        <div>
          <div className="font-semibold text-slate-900 flex items-center gap-2">
            {data.enabled ? <Eye size={16} className="text-emerald-600" /> : <EyeOff size={16} className="text-slate-400" />}
            Banner Visibility
          </div>
          <div className="text-xs text-slate-500 mt-1">When disabled, no customer will see the coupon on the homepage.</div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={!!data.enabled}
          onClick={() => set('enabled', !data.enabled)}
          data-testid="promo-enabled-toggle"
          className={`relative w-14 h-8 rounded-full transition-colors ${data.enabled ? 'bg-emerald-500' : 'bg-slate-300'}`}
        >
          <span className={`absolute top-1 left-1 w-6 h-6 rounded-full bg-white shadow transition-transform ${data.enabled ? 'translate-x-6' : ''}`} />
        </button>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Form */}
        <div className="space-y-4">
          <Field label="Coupon Code" hint="Auto-uppercased. e.g. SAVE10">
            <input
              type="text"
              value={data.code || ''}
              onChange={(e) => set('code', e.target.value)}
              maxLength={32}
              data-testid="promo-code-input"
              className="w-full h-11 px-3 rounded-lg border border-slate-300 font-mono font-bold text-emerald-700 uppercase"
            />
          </Field>

          <Field label="Badge Text" hint="Small pill above the headline (e.g. LIMITED-TIME OFFER)">
            <input
              type="text"
              value={data.badge_text || ''}
              onChange={(e) => set('badge_text', e.target.value)}
              maxLength={40}
              data-testid="promo-badge-input"
              className="w-full h-11 px-3 rounded-lg border border-slate-300"
            />
          </Field>

          <Field label="Headline" hint="Big bold line — keep it punchy">
            <input
              type="text"
              value={data.headline || ''}
              onChange={(e) => set('headline', e.target.value)}
              maxLength={120}
              data-testid="promo-headline-input"
              className="w-full h-11 px-3 rounded-lg border border-slate-300 font-semibold"
            />
          </Field>

          <Field label="Sub-line" hint="Tell customers what services it works on, modern engaging tone">
            <textarea
              value={data.subline || ''}
              onChange={(e) => set('subline', e.target.value)}
              maxLength={240}
              rows={3}
              data-testid="promo-subline-input"
              className="w-full p-3 rounded-lg border border-slate-300"
            />
          </Field>

          <Field label="Bottom Strip / Terms" hint="Min order, expiry hint, etc.">
            <input
              type="text"
              value={data.terms || ''}
              onChange={(e) => set('terms', e.target.value)}
              maxLength={240}
              data-testid="promo-terms-input"
              className="w-full h-11 px-3 rounded-lg border border-slate-300"
            />
          </Field>

          <div className="flex items-center gap-2 text-xs text-slate-400">
            <RotateCcw size={12} />
            Last updated: {data.updated_at ? new Date(data.updated_at).toLocaleString('en-IN') : 'never'}
          </div>
        </div>

        {/* Live preview */}
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-1">
            <BadgePercent size={12} /> Live Preview
          </div>
          <div className="rounded-2xl bg-gradient-to-br from-brand-blue via-brand-blue-light to-brand-blue-dark p-6 shadow-inner">
            {data.enabled ? (
              /* The HeroCouponPromo fetches its own data — so we just re-key it on every change to force a fetch */
              <HeroCouponPromo key={`${data.code}-${data.headline}-${data.subline}-${data.badge_text}-${data.terms}-${data.updated_at}`} />
            ) : (
              <div className="text-center text-white/60 italic py-8 text-sm" data-testid="promo-preview-disabled">
                Banner is currently <strong>disabled</strong> — customers won&apos;t see it.
              </div>
            )}
          </div>
          <p className="text-[11px] text-slate-400 mt-2">
            The preview reflects what visitors will see right after you click <strong>Save Changes</strong>.
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, hint, children }) {
  return (
    <label className="block">
      <div className="text-sm font-semibold text-slate-800 mb-1">{label}</div>
      {hint && <div className="text-[11px] text-slate-500 mb-1.5">{hint}</div>}
      {children}
    </label>
  );
}
